<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';

  let loading = true;
  let success = false;
  let error = '';
  let alreadyVerified = false;
  let expired = false;

  onMount(async () => {
    // Get query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const email = urlParams.get('email');
    const isPasswordReset = urlParams.get('resetpass') === 'true';

    // Validate parameters exist
    if (!code || !email) {
      loading = false;
      error = 'Invalid verification link. Please check your email and try again.';
      return;
    }

    // If this is a password reset, redirect to reset password page with code and email
    if (isPasswordReset) {
      console.log('Password reset verification - redirecting to reset password page');
      navigate(`/reset-password?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`);
      return;
    }

    // Otherwise, proceed with account verification
    try {
      const response = await fetch(`/api/signup/verify?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}`);
      const data = await response.json();

      if (data.success) {
        success = true;
        alreadyVerified = data.alreadyVerified || false;
      } else {
        error = data.error || 'Verification failed. Please try again.';
        expired = data.expired || false;
      }
    } catch (err) {
      console.error('Verification error:', err);
      error = 'An error occurred during verification. Please try again.';
    } finally {
      loading = false;
    }
  });

  function goToLogin() {
    navigate('/login');
  }

  function goToSignup() {
    navigate('/signup');
  }
</script>

<div class="min-h-screen flex items-center justify-center bg-gray-100">
  <div class="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
    <div class="text-center">
      <img class="w-3/4 mx-auto h-24 w-auto rounded-md" src="/images/buildzilla-logo-lg.jpg" alt="Logo">
      <h2 class="mt-6 text-xl font-medium text-gray-900">Account Verification</h2>
    </div>

    {#if loading}
      <!-- Loading State -->
      <div class="flex flex-col items-center justify-center py-12">
        <svg class="animate-spin h-12 w-12 text-[#313b55] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-600">Verifying your account...</p>
      </div>
    {:else if success}
      <!-- Success State -->
      <div class="space-y-6">
        <div class="p-4 bg-green-50 border border-green-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">
                {alreadyVerified ? 'Already Verified' : 'Verification Successful!'}
              </h3>
              <div class="mt-2 text-sm text-green-700">
                {#if alreadyVerified}
                  <p>Your account is already verified. You can log in now.</p>
                {:else}
                  <p>Your email has been verified successfully! You can now log in to your account.</p>
                {/if}
              </div>
            </div>
          </div>
        </div>

        <div>
          <button
            on:click={goToLogin}
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#313b55] hover:bg-[rgb(158,158,158)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(178,178,178)]"
          >
            Go to Login
          </button>
        </div>
      </div>
    {:else}
      <!-- Error State -->
      <div class="space-y-6">
        <div class="p-4 bg-red-50 border border-red-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Verification Failed</h3>
              <div class="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          {#if expired}
            <!-- If expired, offer signup again -->
            <button
              on:click={goToSignup}
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#313b55] hover:bg-[rgb(158,158,158)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(178,178,178)]"
            >
              Sign Up Again
            </button>
          {:else}
            <!-- Otherwise, just offer login -->
            <button
              on:click={goToLogin}
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#313b55] hover:bg-[rgb(158,158,158)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(178,178,178)]"
            >
              Back to Login
            </button>
          {/if}
        </div>
      </div>
    {/if}

    <div class="text-center text-sm text-gray-500 mt-4">
      © 2025 All rights reserved
    </div>
  </div>
</div>


